import React from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { FormattedMessage, FormattedNumber } from 'react-intl'
import { Button, Footer, FlexContainer, Form, FlexedItem } from 'components'
import { PAYMENT_CARD } from 'containers/constants'
import messages from "./messages";

const Section = styled.div`
    border-bottom: 0.5px solid #bcbbc1;
`;
const Container = styled.div`
    display: flex;
    flex-direction: column;
    margin: 16px;
`;
const SectionHeader = styled.h4`
    color: rgba(0, 0, 0, 0.54);
    font-size: 12px;
    font-weight: bold;
    line-height: 16px;
    margin: 0;
`;
const Text = styled.span`
    color: rgba(0, 0, 0, 0.87);
    font-size: 14px;
`;
const Price = styled.span`
    color: rgba(0, 0, 0, 0.87);
    font-size: 14px;
    &:after {
        content: "${(props) => (props.locale === "fr" ? "$" : "")}";
    }
    &:before {
        content: "${(props) => (props.locale === "en" ? "$" : "")}";
    }
`;
const Flexer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
`;

const ItemsHolder = styled.div`
    padding-bottom: 8px;
    padding-top: 8px;
    border-bottom: 0.5px solid #bcbbc1;
    line-height: 22px;
`;
const Holder = styled.div`
    padding-bottom: 8px;
    padding-top: 8px;
    border-bottom: 0.5px solid #bcbbc1;
`;
const PricingHeader = styled.p`
    color: rgba(0, 0, 0, 0.54);
    font-size: 12px;
    line-height: 20px;
    margin: 0;
`;
const PricingValue = styled.p`
    color: rgba(0, 0, 0, 0.54);
    font-size: 12px;
    line-height: 20px;
    margin: 0;
    &:after {
        content: "${(props) => (props.locale === "fr" ? "$" : "")}";
    }
    &:before {
        content: "${(props) => (props.locale === "en" ? "$" : "")}";
    }
`;
const TotalHeader = styled.h4`
    color: rgba(0, 0, 0, 0.87);
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    margin: 0;
    margin-top: 8px;
`;
const TotalValue = styled.span`
    color: rgba(0, 0, 0, 0.87);
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    &:after {
        content: "${(props) => (props.locale === "fr" ? "$" : "")}";
    }
    &:before {
        content: "${(props) => (props.locale === "en" ? "$" : "")}";
    }
`;
const SectionLink = styled.a`
    color: #085eb9;
    font-size: 12px;
    line-height: 16px;
    font-weight: 400;
    margin: 0;
    cursor: pointer;
`;
const CardDetails = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    margin-top: 10px;
`;
const AddressHolder = styled.div`
    margin-top: 10px;
`;
const AddressLine = styled.div`
    color: rgba(0, 0, 0, 0.87);
    font-size: 13px;
    line-height: 20px;
    margin: 0;
`;
const Overlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 86%;
    z-index: 10;
    background-color: rgba(255, 255, 255, 0.5);
`;

const ReviewComponent = (props) => {
    const {
        items,
        locale,
        subTotal,
        taxes: {gst, pst, hst},
        payment,
        address,
        handlePaymentChange,
        handleAddressChange,
        handlePayment,
        isCartProcessing,
    } = props;
    const cardDigits = payment.cardNumber.substr(payment.cardNumber.length - 4);
    const paymentMode = () => {
        if (payment.cardType === PAYMENT_CARD.visa.label) {
            return <img src={`${process.env.SF_HOST_ENV_VAR.trim()}/images/visa.svg`} alt="Visa" />;
        } else if (payment.cardType === PAYMENT_CARD.mastercard.label) {
            return <img src={`${process.env.SF_HOST_ENV_VAR.trim()}/images/mc.svg`} alt="Master Card" />;
        }
        return <img src={`${process.env.SF_HOST_ENV_VAR.trim()}/images/amex.svg`} alt="American Express" />;
    };
    return (
        <FlexContainer>
            {isCartProcessing ? <Overlay /> : null}
            <FlexedItem className="styled-scrollbars">
                <Section>
                    <Container>
                        <SectionHeader>
                            <FormattedMessage {...messages.service} />
                        </SectionHeader>
                        <ItemsHolder>
                            {items.map((item, index) => (
                                <Flexer key={item.sku}>
                                    <Text>{item.name}</Text>
                                    <Price locale={locale}>{<FormattedNumber value={item.price} />}</Price>
                                </Flexer>
                            ))}
                        </ItemsHolder>
                        <Holder>
                            <Flexer>
                                <PricingHeader>
                                    <FormattedMessage {...messages.subTotal} />
                                </PricingHeader>
                                <PricingValue locale={locale}>
                                    {<FormattedNumber value={subTotal} minimumFractionDigits="2" />}
                                </PricingValue>
                            </Flexer>
                            {!!gst && (
                                <Flexer>
                                    <PricingHeader>
                                        <FormattedMessage {...messages.gst} />
                                    </PricingHeader>
                                    <PricingValue locale={locale}>
                                        {<FormattedNumber value={gst} minimumFractionDigits="2" />}
                                    </PricingValue>
                                </Flexer>
                            )}
                            {!!pst && (
                                <Flexer>
                                    <PricingHeader>
                                        <FormattedMessage {...messages.pst} />
                                    </PricingHeader>
                                    <PricingValue locale={locale}>
                                        {<FormattedNumber value={pst} minimumFractionDigits="2" />}
                                    </PricingValue>
                                </Flexer>
                            )}
                            {!!hst && (
                                <Flexer>
                                    <PricingHeader>
                                        <FormattedMessage {...messages.hst} />
                                    </PricingHeader>
                                    <PricingValue locale={locale}>
                                        {<FormattedNumber value={hst} minimumFractionDigits="2" />}
                                    </PricingValue>
                                </Flexer>
                            )}
                        </Holder>
                        <Flexer>
                            <TotalHeader>
                                <FormattedMessage {...messages.total} />
                            </TotalHeader>
                            <TotalValue locale={locale}>
                                {<FormattedNumber value={subTotal + gst + pst + hst} minimumFractionDigits="2" />}
                            </TotalValue>
                        </Flexer>
                    </Container>
                </Section>
                <Section>
                    <Container>
                        <Flexer>
                            <SectionHeader>
                                <FormattedMessage {...messages.payment} />
                            </SectionHeader>
                            <SectionLink onClick={handlePaymentChange}>
                                <FormattedMessage {...messages.change} />
                            </SectionLink>
                        </Flexer>
                        <CardDetails>
                            {paymentMode()}
                            <Text>&nbsp;***&nbsp;{cardDigits}</Text>
                        </CardDetails>
                    </Container>
                </Section>
                <Section>
                    <Container>
                        <Flexer>
                            <SectionHeader>
                                <FormattedMessage {...messages.address} />
                            </SectionHeader>
                            <SectionLink onClick={handleAddressChange}>
                                <FormattedMessage {...messages.change} />
                            </SectionLink>
                        </Flexer>
                        <AddressHolder>
                            <AddressLine>
                                {address.firstName}&nbsp;{address.lastName}
                            </AddressLine>
                            <AddressLine>
                                {address.addressLine1},&nbsp;{address.addressLine2}
                            </AddressLine>
                            <AddressLine>
                                {address.addressLine3}, {address.province}
                            </AddressLine>
                            <AddressLine>
                                {address.country}&nbsp;{address.postalCode}
                            </AddressLine>
                        </AddressHolder>
                    </Container>
                </Section>
            </FlexedItem>
            <Footer>
                <Button
                    text={
                        isCartProcessing ? (
                            <div className="loader loader-bby" />
                        ) : (
                            <FormattedMessage {...messages.pay} />
                        )
                    }
                    onClick={handlePayment}
                    classes="btn btn-filled-bby"
                />
            </Footer>
        </FlexContainer>
    );
};
ReviewComponent.propTypes = {
  items: PropTypes.array,
  locale: PropTypes.string,
  subTotal: PropTypes.number,
  taxes: PropTypes.object,
  address: PropTypes.object,
  payment: PropTypes.object,
  isCartProcessing: PropTypes.bool,
  handlePayment: PropTypes.func,
  handleAddressChange: PropTypes.func,
  handlePaymentChange: PropTypes.func,
}
export default ReviewComponent



// WEBPACK FOOTER //
// ./src/containers/Cart/Review/ReviewComponent.js